/**
 * LinkModal — Smart match + manual search to link Amazon purchases to ML orders.
 * Adapted from amazon/LinkPurchaseModal for the Order Hub.
 * Tab "Inventario" links to internal stock products.
 */
import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/client';
import {
  X, Search, Loader2, Link2, AlertCircle, Package, Warehouse,
} from 'lucide-react';
import { useCan } from '../../lib/rbac';
import type { UnifiedOrder } from './types';

interface LinkModalProps {
  order: UnifiedOrder;
  sellerId: number;
  onClose: () => void;
  onSuccess: () => void;
}

export function LinkModal({ order, sellerId, onClose, onSuccess }: LinkModalProps) {
  const can = useCan();
  // Total de la compra Amazon, precio por unidad y costo del producto en depósito son dinero.
  const canMoney = can('global.view_money');
  const money = (v: any) => (canMoney ? `$${Number(v || 0).toFixed(2)}` : '—');
  // Pack ML = varias órdenes (productos distintos) en un solo envío. El link va
  // SIEMPRE contra UNA orden concreta — si es pack, el usuario elige cuál.
  // Default: la primera orden sin compra Amazon concretada (el caso típico).
  const packOrderIds = Array.from(new Set(order.items.map(i => i.ml_order_id).filter(Boolean)));
  const isPack = packOrderIds.length > 1;
  const [targetOrderId, setTargetOrderId] = useState<number | null | undefined>(() => {
    if (!isPack) return order.ml_order_id || order.pack_id;
    const linked = new Set(
      (order.purchases || []).filter(p => p.amazon_order_id).map(p => p.ml_order_id)
    );
    return packOrderIds.find(id => !linked.has(id)) ?? packOrderIds[0];
  });
  const mlOrderId = targetOrderId;
  // Items y compras SOLO de la orden elegida — el matching por ASIN y las
  // cantidades salen de acá; mezclar las del pack entero vinculaba el producto
  // de una orden hermana a la orden equivocada (bug 2026-08-08).
  const targetItems = isPack ? order.items.filter(i => i.ml_order_id === mlOrderId) : order.items;
  const targetPurchases = isPack
    ? (order.purchases || []).filter(p => p.ml_order_id === mlOrderId)
    : (order.purchases || []);
  const [tab, setTab] = useState<'smart' | 'search' | 'stock'>('smart');
  const [candidates, setCandidates] = useState<any[]>([]);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState('');

  // Stock tab state
  const [stockCandidates, setStockCandidates] = useState<any[]>([]);
  const [stockSearchResults, setStockSearchResults] = useState<any[]>([]);
  const [stockSearchQuery, setStockSearchQuery] = useState('');
  const [stockLoading, setStockLoading] = useState(false);
  const [stockSearchMode, setStockSearchMode] = useState(false);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Load smart match candidates (se recarga al cambiar la orden elegida del pack)
  useEffect(() => {
    if (!mlOrderId) return;
    setCandidates([]);
    setStockCandidates([]);
    setStockSearchResults([]);
    setError('');
    (async () => {
      setLoading(true);
      try {
        const res = await api.get<any>(`/amazon/link/candidates?seller_id=${sellerId}&ml_order_id=${mlOrderId}`);
        if (res.success && res.data) {
          setCandidates(res.data.candidates || []);
        }
      } catch (err) {
        console.error('[LinkModal] Failed to load candidates:', err);
      }
      setLoading(false);
    })();
  }, [sellerId, mlOrderId]);

  const doSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    try {
      const res = await api.get<any[]>(`/amazon/link/search?seller_id=${sellerId}&q=${encodeURIComponent(searchQuery)}`);
      if (res.success && res.data) setSearchResults(res.data);
    } catch (err) {
      console.error('[LinkModal] Search failed:', err);
    }
    setLoading(false);
  }, [sellerId, searchQuery]);

  // Load stock candidates when stock tab is selected
  useEffect(() => {
    if (tab !== 'stock' || !mlOrderId || stockCandidates.length > 0) return;
    (async () => {
      setStockLoading(true);
      try {
        const res = await api.get<any>(`/internal-stock/link/candidates?seller_id=${sellerId}&ml_order_id=${mlOrderId}`);
        if (res.success && res.data) {
          setStockCandidates(res.data.candidates || []);
        }
      } catch (err) {
        console.error('[LinkModal] Stock candidates failed:', err);
      }
      setStockLoading(false);
    })();
  }, [tab, sellerId, mlOrderId]);

  const doStockSearch = useCallback(async () => {
    if (!stockSearchQuery.trim()) return;
    setStockLoading(true);
    setStockSearchMode(true);
    try {
      const res = await api.get<any[]>(`/internal-stock/link/search?seller_id=${sellerId}&q=${encodeURIComponent(stockSearchQuery)}`);
      if (res.success && res.data) setStockSearchResults(res.data);
    } catch (err) {
      console.error('[LinkModal] Stock search failed:', err);
    }
    setStockLoading(false);
  }, [sellerId, stockSearchQuery]);

  const linkStock = async (stockProduct: any) => {
    setLinking(true);
    setError('');

    // Use the target order item's quantity
    const qty = targetItems[0]?.quantity || 1;

    try {
      const res = await api.post('/internal-stock/link/order', {
        seller_id: sellerId,
        ml_order_id: mlOrderId,
        stock_product_id: stockProduct.id,
        quantity: qty,
      });

      if (res.success) {
        onSuccess();
      } else {
        setError((res as any).error || 'Error al vincular con inventario');
      }
    } catch (err: any) {
      setError(err?.message || 'Error al vincular con inventario');
    } finally {
      setLinking(false);
    }
  };

  const linkOrder = async (candidate: any, specificAsin?: string) => {
    setLinking(true);
    setError('');

    // Build mappings — only link specific ASIN if provided, otherwise match by ASIN
    // Use ML order quantity (what we're selling), NOT Amazon quantity (bulk purchase may cover multiple ML orders)
    let mappings: Array<{ amazon_order_id: string; asin: string; quantity: number }>;

    if (specificAsin) {
      // Link only this specific ASIN — fallback to first item when ASIN was changed on purchase
      const mlItem = targetItems.find(i => {
        const sku = (i.seller_sku || '').match(/^([A-Z0-9]{10})/)?.[1];
        return sku === specificAsin;
      }) || targetItems[0];
      mappings = [{
        amazon_order_id: candidate.amazon_order_id,
        asin: specificAsin,
        quantity: mlItem?.quantity || 1,
      }];
    } else {
      // Match all ASINs between target ML order and Amazon order (includes purchase ASINs)
      const lineItems = (candidate.line_items || []).filter(
        (li: any) => allOrderAsins.has(li.asin) && (li.available_quantity ?? (li.quantity - (li.linked_quantity || 0))) > 0
      );
      mappings = lineItems.length > 0
        ? lineItems.map((li: any) => {
            const mlItem = targetItems.find(i => (i.seller_sku || '').match(/^([A-Z0-9]{10})/)?.[1] === li.asin)
              || targetItems[0]; // fallback when ASIN was changed on purchase
            return {
              amazon_order_id: candidate.amazon_order_id,
              asin: li.asin,
              quantity: mlItem?.quantity || 1,
            };
          })
        : [{
            amazon_order_id: candidate.amazon_order_id,
            asin: targetItems[0]?.seller_sku || '',
            quantity: targetItems[0]?.quantity || 1,
          }];
    }

    const res = await api.post('/amazon/link', {
      seller_id: sellerId,
      ml_order_id: mlOrderId,
      mappings,
    });

    if (res.success) {
      onSuccess();
    } else {
      setError((res as any).error || 'Error al vincular');
    }
    setLinking(false);
  };

  // Collect all relevant ASINs: from TARGET order items AND its purchases (user
  // may have changed ASIN). Pack: NUNCA mezclar ASINs de órdenes hermanas — eso
  // vinculaba la compra del producto de otra orden a esta (bug 2026-08-08).
  const allOrderAsins = new Set([
    ...targetItems.map(i => (i.seller_sku || '').match(/^([A-Z0-9]{10})/)?.[1]).filter(Boolean),
    ...targetPurchases.map(p => p.asin).filter(Boolean),
  ]);

  const displayList = tab === 'smart' ? candidates : searchResults;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[var(--color-bg)] rounded-xl border border-[var(--color-border)] w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            <Link2 size={18} className="text-[var(--color-primary)]" />
            <h3 className="font-bold text-[var(--color-text)]">
              Vincular Compra {'\u2192'} Orden #{mlOrderId}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--color-bg-tertiary)]">
            <X size={18} className="text-[var(--color-text-secondary)]" />
          </button>
        </div>

        {/* Pack: selector de orden destino \u2014 el link es SIEMPRE contra una orden */}
        {isPack && (
          <div className="px-4 pt-3 pb-2 bg-[var(--color-bg-secondary)] border-b border-[var(--color-border)]">
            <p className="text-xs text-[var(--color-text-tertiary)] mb-1.5 uppercase font-medium">
              Carrito con {packOrderIds.length} \u00F3rdenes \u2014 \u00BFa cu\u00E1l vincul\u00E1s?
            </p>
            <div className="flex flex-wrap gap-2">
              {packOrderIds.map((oid) => {
                const firstItem = order.items.find(i => i.ml_order_id === oid);
                const hasLinked = (order.purchases || []).some(
                  p => p.ml_order_id === oid && p.amazon_order_id
                );
                const active = oid === targetOrderId;
                return (
                  <button key={oid} onClick={() => setTargetOrderId(oid)}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-left transition-colors ${
                      active
                        ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)]'
                        : 'border-[var(--color-border)] bg-[var(--color-bg-tertiary)] hover:border-[var(--color-primary)]'
                    }`}>
                    {firstItem?.thumbnail && (
                      <img src={firstItem.thumbnail} alt="" className="w-7 h-7 rounded object-cover" />
                    )}
                    <span className="flex flex-col">
                      <span className="text-xs text-[var(--color-text)] max-w-[220px] truncate">
                        {firstItem?.title || firstItem?.seller_sku || oid}
                      </span>
                      <span className="text-[10px] text-[var(--color-text-tertiary)] font-mono">
                        #{oid}{hasLinked ? ' \u00B7 ya vinculada' : ''}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Order summary \u2014 items de la orden destino */}
        <div className="px-4 pt-3 pb-2 bg-[var(--color-bg-secondary)] border-b border-[var(--color-border)]">
          <p className="text-xs text-[var(--color-text-tertiary)] mb-1 uppercase font-medium">Items de la orden ML:</p>
          <div className="flex flex-wrap gap-2">
            {targetItems.map((item, i) => (
              <div key={i} className="flex items-center gap-2 px-2 py-1 bg-[var(--color-bg-tertiary)] rounded">
                {item.thumbnail && (
                  <img src={item.thumbnail} alt="" className="w-6 h-6 rounded object-cover" />
                )}
                <span className="text-xs font-mono text-[var(--color-text)]">
                  {item.seller_sku?.slice(0, 12) || item.item_id} {'\u00D7'} {item.quantity}
                </span>
                <span className="text-xs text-[var(--color-text-secondary)]">
                  {money(item.unit_price)}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-1 text-xs text-[var(--color-text-tertiary)]">
            Total: <span className="font-medium text-[var(--color-text)]">{canMoney ? `${order.currency_id} ${money(order.paid_amount)}` : '—'}</span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[var(--color-border)]">
          <button onClick={() => setTab('smart')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
              tab === 'smart'
                ? 'text-[var(--color-primary)] border-b-2 border-[var(--color-primary)]'
                : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text)]'
            }`}>
            Smart Match {candidates.length > 0 && `(${candidates.length})`}
          </button>
          <button onClick={() => setTab('search')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
              tab === 'search'
                ? 'text-[var(--color-primary)] border-b-2 border-[var(--color-primary)]'
                : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text)]'
            }`}>
            Buscar Manual
          </button>
          <button onClick={() => setTab('stock')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
              tab === 'stock'
                ? 'text-[var(--color-primary)] border-b-2 border-[var(--color-primary)]'
                : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text)]'
            }`}>
            Inventario {stockCandidates.length > 0 && `(${stockCandidates.length})`}
          </button>
        </div>

        {/* Search bar (manual tab) */}
        {tab === 'search' && (
          <div className="flex gap-2 p-3 border-b border-[var(--color-border)]">
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Amazon Order ID, ASIN, o titulo..."
              className="flex-1 px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
              onKeyDown={(e) => e.key === 'Enter' && doSearch()} />
            <button onClick={doSearch} className="px-3 py-2 bg-[var(--color-primary)] text-white rounded-lg text-sm hover:bg-[var(--color-primary-hover)] transition-colors">
              <Search size={16} />
            </button>
          </div>
        )}

        {/* Search bar (stock tab — toggle between auto-match and search) */}
        {tab === 'stock' && (
          <div className="flex gap-2 p-3 border-b border-[var(--color-border)]">
            {stockSearchMode ? (
              <>
                <button onClick={() => { setStockSearchMode(false); setStockSearchResults([]); setStockSearchQuery(''); }}
                  className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text)] transition-colors whitespace-nowrap">
                  ← Auto Match
                </button>
                <input type="text" value={stockSearchQuery} onChange={(e) => setStockSearchQuery(e.target.value)}
                  placeholder="ASIN, SKU interno, o nombre..."
                  className="flex-1 px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
                  onKeyDown={(e) => e.key === 'Enter' && doStockSearch()} />
                <button onClick={doStockSearch} className="px-3 py-2 bg-[var(--color-primary)] text-white rounded-lg text-sm hover:bg-[var(--color-primary-hover)] transition-colors">
                  <Search size={16} />
                </button>
              </>
            ) : (
              <div className="flex items-center justify-between w-full">
                <span className="text-xs text-[var(--color-text-secondary)]">
                  Productos con stock que coinciden por ASIN
                </span>
                <button onClick={() => setStockSearchMode(true)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text)] transition-colors">
                  <Search size={12} /> Buscar otro
                </button>
              </div>
            )}
          </div>
        )}

        {/* Results — Amazon tabs */}
        {(tab === 'smart' || tab === 'search') && (
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle size={16} className="text-red-500" />
              <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" />
            </div>
          ) : displayList.length === 0 ? (
            <div className="text-center py-12 text-[var(--color-text-tertiary)]">
              <Package size={32} className="mx-auto mb-2 opacity-50" />
              <p>{tab === 'smart' ? 'No se encontraron matches automaticos' : 'Busca por Order ID, ASIN, o titulo'}</p>
            </div>
          ) : (
            displayList.map((candidate: any) => (
              <div key={candidate.amazon_order_id}
                className="border border-[var(--color-border)] rounded-lg p-3 hover:border-[var(--color-primary)] transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="font-mono text-sm font-medium text-[var(--color-text)]">{candidate.amazon_order_id}</span>
                    {candidate.match_score !== undefined && (
                      <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                        candidate.match_score >= 80 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : candidate.match_score >= 50 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                      }`}>
                        {candidate.match_score}% match
                      </span>
                    )}
                  </div>
                  <button onClick={() => linkOrder(candidate)} disabled={linking || !(candidate.line_items || []).some(
                    (li: any) => allOrderAsins.has(li.asin)
                      && (li.available_quantity ?? (li.quantity - (li.linked_quantity || 0))) > 0
                  )}
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium disabled:opacity-50 transition-colors"
                    title="Vincular todos los ASINs que matchean">
                    {linking ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />}
                    Vincular Todos
                  </button>
                </div>
                <div className="flex gap-4 text-xs text-[var(--color-text-secondary)] mb-2">
                  <span>{candidate.order_date ? new Date(candidate.order_date).toLocaleDateString() : '\u2014'}</span>
                  <span>{candidate.order_status}</span>
                  <span className="font-medium">{money(candidate.net_total)}</span>
                  {candidate.buyer_name && <span>{candidate.buyer_name}</span>}
                </div>
                {/* Line items with availability */}
                <div className="space-y-1">
                  {(candidate.line_items || []).map((li: any, idx: number) => {
                    const available = li.available_quantity ?? (li.quantity - (li.linked_quantity || 0));
                    const total = li.quantity || 1;
                    const linked = li.linked_quantity || 0;
                    const isMatch = allOrderAsins.has(li.asin);
                    return (
                      <div key={idx} className={`flex items-center gap-3 text-xs rounded px-2 py-1.5 ${
                        isMatch ? 'bg-green-50 dark:bg-green-900/15 border border-green-200 dark:border-green-800/40' : 'bg-[var(--color-bg-secondary)]'
                      }`}>
                        <span className={`font-mono font-medium ${isMatch ? 'text-green-600 dark:text-green-400' : 'text-teal-500'}`}>{li.asin}</span>
                        <span className="text-[var(--color-text)] flex-1 truncate">{li.title}</span>
                        <span className="font-medium">{money(li.unit_price)}</span>
                        {/* Availability indicator */}
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          available >= total ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : available > 0 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                          : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                        }`} title={`${linked} de ${total} vinculadas, ${available} disponibles`}>
                          {available}/{total}
                        </span>
                        {/* Per-ASIN link button when this ASIN matches the ML order */}
                        {isMatch && available > 0 && (
                          <button onClick={(e) => { e.stopPropagation(); linkOrder(candidate, li.asin); }}
                            disabled={linking}
                            className="px-2 py-0.5 bg-green-600 hover:bg-green-700 text-white rounded text-[10px] font-medium disabled:opacity-50 transition-colors whitespace-nowrap">
                            {linking ? '...' : 'Vincular'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
        )}

        {/* Results — Stock tab */}
        {tab === 'stock' && (
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle size={16} className="text-red-500" />
              <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
            </div>
          )}

          {stockLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[var(--color-primary)]" />
            </div>
          ) : (stockSearchMode ? stockSearchResults : stockCandidates).length === 0 ? (
            <div className="text-center py-12 text-[var(--color-text-tertiary)]">
              <Warehouse size={32} className="mx-auto mb-2 opacity-50" />
              <p>{stockSearchMode ? 'Sin resultados — busca por ASIN, SKU, o nombre' : 'No hay productos con stock que coincidan por ASIN'}</p>
            </div>
          ) : (
            (stockSearchMode ? stockSearchResults : stockCandidates).map((sp: any) => {
              const isMatch = allOrderAsins.has(sp.asin);
              return (
                <div key={sp.id}
                  className={`border rounded-lg p-3 transition-colors ${
                    sp.already_linked
                      ? 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-900/10'
                      : 'border-[var(--color-border)] hover:border-[var(--color-primary)]'
                  }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {sp.image_url && (
                        <img src={sp.image_url} alt="" className="w-8 h-8 rounded object-cover" />
                      )}
                      <div>
                        <span className="text-sm font-medium text-[var(--color-text)] line-clamp-1">{sp.title || sp.asin}</span>
                        <div className="flex items-center gap-2 mt-0.5">
                          {sp.asin && (
                            <span className={`font-mono text-xs ${isMatch ? 'text-green-600 dark:text-green-400 font-semibold' : 'text-teal-500'}`}>
                              {sp.asin}
                            </span>
                          )}
                          {sp.internal_sku && (
                            <span className="text-xs text-[var(--color-text-secondary)]">SKU: {sp.internal_sku}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {sp.already_linked ? (
                      <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                        Ya vinculado
                      </span>
                    ) : (
                      <button onClick={() => linkStock(sp)} disabled={linking || sp.qty_available <= 0}
                        className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium disabled:opacity-50 transition-colors">
                        {linking ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />}
                        Vincular
                      </button>
                    )}
                  </div>
                  <div className="flex gap-4 text-xs text-[var(--color-text-secondary)]">
                    <span>Costo: <span className="font-medium text-[var(--color-text)]">{money(sp.unit_cost)}</span></span>
                    <span className={`font-medium ${
                      sp.qty_available > 5 ? 'text-green-600 dark:text-green-400'
                      : sp.qty_available > 0 ? 'text-yellow-600 dark:text-yellow-400'
                      : 'text-red-500'
                    }`}>
                      Disponible: {sp.qty_available ?? 0}
                    </span>
                    {sp.supplier_name && <span>Proveedor: {sp.supplier_name}</span>}
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                      style={{
                        backgroundColor: 'color-mix(in srgb, var(--color-success) 15%, transparent)',
                        color: 'var(--color-success)',
                      }}>
                      Stock
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
        )}
      </div>
    </div>
  );
}
